import { useQueryClient } from '@tanstack/react-query'
import { useWallet } from '@txnlab/use-wallet-react'
import dynamic from 'next/dynamic'
import * as React from 'react'
import { isMobile } from 'react-device-detect'
import toast from 'react-hot-toast'
import { IconType } from 'react-icons'
import { BsImage } from 'react-icons/bs'
import { HiOutlineCheckCircle, HiUserCircle } from 'react-icons/hi'
import { NfdRecord, nfdVerifyRequest } from '@/api/api-client'
import { usePatchUpdateProperties } from '@/api/hooks/usePatchUpdateProperties'
import useVerifyConfirm from '@/api/hooks/useVerifyConfirm'
import AssetMedia from '@/components/AssetMedia'
import galleryImageLoader from '@/helpers/galleryImageLoader'
import { capitalizeFirstLetter } from '@/helpers/strings'
import { clsxMerge } from '@/helpers/utilities'
import useErrorToast from '@/hooks/useErrorToast'
import { useNfdPolling } from '@/hooks/useNfdPolling'
import { Asset } from '@/types/assets'
import { Nfts } from './Nfts'
import { NftDetails } from './NftDetails'

const Modal = dynamic(() => import('components/Modal'), { ssr: false })

type Field = 'avatar' | 'banner'

const ICONS: { [key in Field | 'default']: IconType } = {
  avatar: HiUserCircle,
  banner: BsImage,
  default: HiOutlineCheckCircle
}

enum UpdateStatus {
  IDLE,
  UPDATING,
  UPDATED,
  VERIFYING,
  VERIFIED
}

interface AvatarBannerModalProps {
  nfd: NfdRecord
  field: Field | null
  setField: (field: Field | null) => void
  asset?: Asset
}

export default function AvatarBannerModal({ nfd, field, setField, asset }: AvatarBannerModalProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [status, setStatus] = React.useState<UpdateStatus>(UpdateStatus.IDLE)
  const [selectedAsset, setSelectedAsset] = React.useState<Asset | null>(asset ?? null)
  const [actionLabel, setActionLabel] = React.useState<string>('')

  const handleClose = React.useCallback(() => {
    setField(null)
    setTimeout(() => {
      setActionLabel('')
      setStatus(UpdateStatus.IDLE)
      if (!asset) {
        setSelectedAsset(null)
      }
    }, 300)
  }, [asset, setField])

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      handleClose()
    }
    setIsOpen(isOpen)
  }

  const handleCancel = () => {
    if (asset) {
      handleClose()
    } else {
      setSelectedAsset(null)
    }
  }

  React.useEffect(() => {
    setIsOpen(!!field)
    if (field) {
      setActionLabel(`Update ${field}`)
    }
  }, [field])

  const { activeAddress } = useWallet()
  const queryClient = useQueryClient()
  const handleError = useErrorToast()
  const eventId = React.useId()

  const successCondition = (nfd: NfdRecord) => {
    if (!selectedAsset || !field) {
      return false
    }
    return nfd.properties?.userDefined?.[field] === selectedAsset.id.toString()
  }

  const {
    startPolling,
    status: fieldUpdatedStatus,
    error: fieldUpdatedError
  } = useNfdPolling(nfd.name, successCondition, 'nfdFieldUpdated', eventId)

  const updateField = usePatchUpdateProperties({
    onMutate: () => {
      setStatus(UpdateStatus.UPDATING)
    },
    onSuccess() {
      toast.loading(`Confirming ${field ?? ''} update...`, { id: eventId })
      startPolling()
    },
    onError(error) {
      handleError(error, { toastId: eventId })
      setStatus(UpdateStatus.IDLE)
    },
    toasts: {
      success: `${capitalizeFirstLetter(field ?? '')} successfully updated`
    }
  })

  const handleUpdateField = () => {
    try {
      if (!activeAddress) {
        throw new Error('No active address')
      }

      if (!field) {
        throw new Error('Field not defined')
      }

      if (!selectedAsset) {
        throw new Error('Asset not selected')
      }

      updateField.mutate({
        name: nfd.name,
        body: {
          sender: activeAddress,
          properties: {
            userDefined: { [field]: selectedAsset.id.toString() }
          }
        }
      })
    } catch (error) {
      handleError(error, { toastId: eventId })
    }
  }

  React.useEffect(() => {
    const handleNfdFieldUpdated = (event: CustomEvent) => {
      if (event.detail.name === nfd.name && event.detail.id === eventId) {
        setStatus(UpdateStatus.UPDATED)
        toast.dismiss(eventId)
      }
    }
    window.addEventListener('nfdFieldUpdated', handleNfdFieldUpdated as EventListener)

    return () => {
      window.removeEventListener('nfdFieldUpdated', handleNfdFieldUpdated as EventListener)
    }
  }, [nfd.name, eventId])

  React.useEffect(() => {
    if (fieldUpdatedStatus === 'error' && fieldUpdatedError) {
      handleError(fieldUpdatedError, { toastId: eventId })
      setStatus(UpdateStatus.IDLE)
    }
  }, [handleError, fieldUpdatedError, fieldUpdatedStatus, eventId])

  const verifyField = useVerifyConfirm({
    loading: `Verifying ${field ?? ''}...`,
    success: `${capitalizeFirstLetter(field ?? '')} successfully verified!`,
    error: `Verification failed`
  })

  const handleVerifyField = React.useCallback(async () => {
    try {
      if (!activeAddress) {
        throw new Error('No active address')
      }

      if (!field) {
        throw new Error('Field not defined')
      }

      if (!selectedAsset) {
        throw new Error('Asset not selected')
      }

      setStatus(UpdateStatus.VERIFYING)

      const { data } = await nfdVerifyRequest({
        name: nfd.name,
        fieldToVerify: field,
        sender: activeAddress
      })

      if (data.id === undefined || data.challenge === undefined) {
        throw new Error('Verification request failed')
      }

      const payload = {
        id: data.id,
        challenge: data.challenge,
        name: nfd.name,
        field,
        asset: selectedAsset
      }

      verifyField.mutate(payload, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['name', nfd.name] })
          setStatus(UpdateStatus.VERIFIED)
        },
        onError: (error) => {
          handleError(error)
          setStatus(UpdateStatus.UPDATED)
        },
        onSettled: () => {
          handleClose()
        }
      })
    } catch (error) {
      handleError(error)
    }
  }, [
    activeAddress,
    field,
    handleClose,
    handleError,
    nfd.name,
    queryClient,
    selectedAsset,
    verifyField
  ])

  React.useEffect(() => {
    if (status === UpdateStatus.UPDATED) {
      handleVerifyField()
    }
  }, [handleVerifyField, status])

  const renderSelectedAsset = () => {
    return (
      <>
        {selectedAsset && (
          <div className="flex flex-col items-center justify-center gap-6 sm:flex-row sm:gap-8">
            <div className="relative aspect-square w-48 overflow-hidden rounded-xl xl:w-64">
              <AssetMedia
                src={selectedAsset.imageUrl}
                alt={selectedAsset.id.toString()}
                className="object-cover w-full h-full"
                loader={galleryImageLoader}
                sizes="(max-width: 640px) 100vw, 360px"
                fill
                options={{ optimizeGifs: true, stateBgColor: 'transparent' }}
                videoJsOptions={{
                  preload: isMobile ? 'auto' : 'metadata',
                  controls: isMobile,
                  fluid: true,
                  fill: true
                }}
              />
            </div>
            <div className="flex-1 w-full max-w-[20rem] sm:max-w-none">
              <h2 className="text-xl font-semibold leading-7 truncate text-gray-900 dark:text-gray-100">
                <span className="hidden sm:sr-only">Details for </span>
                {selectedAsset.name}
              </h2>
              <NftDetails asset={selectedAsset} className="hidden sm:block mt-6" />
            </div>
          </div>
        )}
        <div className="pt-4 sm:pt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3 sm:my-1">
          <button
            type="button"
            className="inline-flex w-full justify-center rounded-md bg-brand-600 px-4 py-2.5 font-medium text-white shadow-sm hover:bg-brand-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 sm:col-start-2 disabled:opacity-25"
            onClick={handleUpdateField}
            disabled={status > UpdateStatus.IDLE}
          >
            {status > UpdateStatus.IDLE ? 'Updating...' : actionLabel}
          </button>
          <button
            type="button"
            className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2.5 font-medium text-gray-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 hover:bg-gray-50 sm:col-start-1 sm:mt-0 dark:border-transparent dark:bg-gray-750 dark:text-gray-300 dark:hover:bg-gray-700 dark:focus:ring-gray-100 disabled:opacity-25"
            onClick={handleCancel}
            disabled={status > UpdateStatus.IDLE}
          >
            {asset ? 'Cancel' : 'Back'}
          </button>
        </div>
      </>
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={handleOpenChange}
      className="max-w-2xl xl:max-w-3xl"
      icon={field ? ICONS[field] : ICONS.default}
      title={actionLabel}
    >
      <div className="relative mt-6 h-96">
        {!asset && (
          <div
            className={clsxMerge(
              'absolute inset-0 overflow-y-auto',
              selectedAsset ? 'pointer-events-none' : ''
            )}
          >
            <Nfts nfd={nfd} isOpen={isOpen} setSelectedAsset={setSelectedAsset} />
          </div>
        )}
        <div
          className={clsxMerge(
            'absolute inset-0 flex-col justify-between h-96 bg-white dark:bg-gray-900',
            selectedAsset ? 'flex' : 'hidden'
          )}
        >
          {renderSelectedAsset()}
        </div>
      </div>
    </Modal>
  )
}
